import { Carousel } from '@mantine/carousel';
import { Anchor, Badge, Box, Card, Grid, Image, rem, Stack, Text, Title } from '@mantine/core';
import { IconChevronLeft, IconChevronRight } from '@tabler/icons-react';
import { useParams } from 'react-router';

const ProductCarouselCard = ({ product }) => {
  const { accountId, domainId } = useParams();
  const href = `#/accounts/${accountId}/domains/${domainId}/products/${product.id}`;

  return (
    <Grid.Col span={{ base: 12, md: 3 }}>
      <Stack gap="xs" align="center" style={{ borderRight: '1px solid rgba(136, 136, 136, 0.2)', height: '100%' }}>
        <Box style={{ width: '100%', maxWidth: 150, aspectRatio: '1', overflow: 'hidden' }}>
          <a href={href}>
            <Image
              src={product?.imageUrl}
              alt={product?.name}
              referrerPolicy="no-referrer"
              fit="contain"
              h={150}
            />
          </a>
        </Box>

        <Stack gap={4} align="center" style={{ flex: 1 }}>
          <Text size="xs" c="dimmed">{product?.id}</Text>
          <Text
            size="sm"
            fw={600}
            ta="center"
            lineClamp={2}
            style={{ minHeight: 40 }}
          >
            <Anchor href={href} c="inherit">
              {product?.name}
            </Anchor>
          </Text>
          {product?.available ? (
            <Badge color="green" variant="light" size="sm">In stock</Badge>
          ) : (
            <Badge color="red" variant="light" size="sm">Out of stock</Badge>
          )}
          {product?.discountPrice ? (
            <Stack gap={0} align="center">
              <Text size="xs" td="line-through" c="dimmed">
                {product?.listPrice + ' ' + product?.currency}
              </Text>
              <Text size="sm" fw={600} c="red">
                {product?.discountPrice + ' ' + product?.currency}
              </Text>
            </Stack>
          ) : (
            <Text size="sm" fw={500}>
              {product?.listPrice + ' ' + product?.currency}
            </Text>
          )}
        </Stack>
      </Stack>
    </Grid.Col>
  );
};

const RecommenderResponse = ({ recommendation, disableWrapper = false }) => {
  if (recommendation === undefined) {
    return '';
  }

  const products = recommendation.response;

  const chunkedProducts = products?.reduce((acc, product, index) => {
    if (index % 4 === 0) {
      acc.push([]);
    }
    acc[acc.length - 1].push(product);
    return acc;
  }, []);

  const hasMultiplePages = chunkedProducts?.length > 1;

  const carousel = (
    <Carousel
      withIndicators={false}
      withControls={hasMultiplePages}
      loop={hasMultiplePages}
      slideSize="100%"
      slideGap={0}
      align="start"
      nextControlIcon={<IconChevronRight style={{ width: rem(24), height: rem(24) }} />}
      previousControlIcon={<IconChevronLeft style={{ width: rem(24), height: rem(24) }} />}
      styles={{
        root: {
          position: 'relative',
        },
        control: {
          backgroundColor: 'var(--mantine-color-gray-0)',
          border: '1px solid var(--mantine-color-gray-3)',
          boxShadow: 'var(--mantine-shadow-sm)',
          width: rem(36),
          height: rem(36),
          opacity: 1,
          '&[data-inactive]': {
            opacity: 0,
            cursor: 'default',
          },
        },
        controls: {
          position: 'absolute',
          top: '50%',
          transform: 'translateY(-50%)',
          left: 0,
          right: 0,
          padding: '0 4px',
        },
      }}
    >
      {chunkedProducts?.map((chunk, index) => (
        <Carousel.Slide key={index}>
          <Grid px="xl">
            {chunk.map((product, innerIndex) => (
              <ProductCarouselCard key={innerIndex} product={product} />
            ))}
          </Grid>
        </Carousel.Slide>
      ))}
    </Carousel>
  );

  if (disableWrapper) {
    return carousel;
  }

  return (
    <Card mb="sm" withBorder>
      <Card.Section inheritPadding py="sm">
        <Title order={5} mb="sm">{recommendation.name}</Title>
        {carousel}
      </Card.Section>
    </Card>
  );
};

export default RecommenderResponse;
